import { useState, useEffect } from 'react';
import { X, CheckCircle, ArrowRight, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_CONFIG } from '../utils/productionConfig';

interface PromotionalOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPayment: (orderId: string) => void;
}

interface PromotionalOrder {
  _id: string;
  planId: {
    _id: string;
    name: string;
    description: string;
    duration: number;
    price: number;
    features?: any;
    customFeatures?: string[];
  };
  originalPrice: number;
  discountedPrice: number;
  discountPercentage: number;
  extraContacts: number;
  extraValidity: number;
  validUntil: string;
  remainingTimeInSeconds: number;
  campaignName?: string;
  notes?: string;
}

export default function PromotionalOrderModal({ isOpen, onClose, onPayment }: PromotionalOrderModalProps) {
  const [promotionalOrder, setPromotionalOrder] = useState<PromotionalOrder | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [gstSettings, setGstSettings] = useState<any>(null);

  // Fetch active promotional order
  useEffect(() => {
    if (isOpen) {
      fetchActivePromotionalOrder();
      fetchGSTSettings();
    }
  }, [isOpen]);

  const fetchGSTSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_CONFIG.apiUrl}/gst-settings`);
      setGstSettings(data.data);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchActivePromotionalOrder = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_CONFIG.apiUrl}/promotional-orders/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000 // 5 second timeout
        }
      );

      if (response.data.success && response.data.data) {
        const order = response.data.data;
        setPromotionalOrder(order);
        setRemainingTime(order.remainingTimeInSeconds || 0);
      } else {
        setPromotionalOrder(null);
        onClose(); // Close if no active order
      }
    } catch (error) {
      // Silently fail and close modal if API doesn't exist or times out
      setPromotionalOrder(null);
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  // Countdown timer
  useEffect(() => {
    if (!promotionalOrder || remainingTime <= 0) return;

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose(); // Auto-close when timer expires
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [promotionalOrder, remainingTime, onClose]);

  // Format time for countdown boxes
  const getCountdownBoxes = () => {
    const days = Math.floor(remainingTime / 86400);
    const hours = Math.floor((remainingTime % 86400) / 3600);
    const minutes = Math.floor((remainingTime % 3600) / 60);
    const seconds = remainingTime % 60;

    return { days, hours, minutes, seconds };
  };

  const handlePayNow = async () => {
    if (!promotionalOrder) return;

    setIsProcessing(true);
    try {
      // Call payment handler with promotional order
      onPayment(promotionalOrder._id);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen || !promotionalOrder) return null;

  const { planId, originalPrice, discountedPrice, discountPercentage, extraContacts, extraValidity } = promotionalOrder;
  const countdown = getCountdownBoxes();
  
  // Calculate GST
  const gstAmount = gstSettings?.isEnabled ? (discountedPrice * gstSettings.gstPercentage) / 100 : 0;
  const totalAmount = discountedPrice + gstAmount;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal - Single Column Centered */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-[95vw] sm:max-w-lg md:max-w-2xl animate-in zoom-in-95 fade-in duration-200">
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
          
          {/* Content */}
          <div className="relative p-6 sm:p-8">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
            >
              <X className="size-5 text-gray-600" />
            </button>

            {/* Header */}
            <div className="mb-6">
              <div className="inline-block bg-rose-600 text-white px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wide mb-3">
                LIMITED TIME OFFER
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Find your soulmate <span className="text-rose-600">faster.</span>
              </h2>
              <p className="text-gray-600 text-sm sm:text-base">
                Unlock Premium Features today! Get <span className="font-bold text-rose-600">{Math.round(discountPercentage)}% OFF</span> + {extraContacts} Extra Phone Numbers included in the {planId.name}.
              </p>
            </div>

            {/* Countdown Timer */}
            <div className="mb-6">
              <p className="text-gray-500 text-xs sm:text-sm uppercase tracking-wide mb-3">Offer Expires In:</p>
              <div className="grid grid-cols-4 gap-2 sm:gap-3">
                <TimeBox value={countdown.days} label="DAYS" />
                <TimeBox value={countdown.hours} label="HOURS" />
                <TimeBox value={countdown.minutes} label="MINS" />
                <TimeBox value={countdown.seconds} label="SECS" />
              </div>
            </div>

            {/* Price Section */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 rounded-xl p-4 sm:p-5 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <p className="text-gray-500 text-sm line-through mb-1">₹{originalPrice.toLocaleString()}</p>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <p className="text-3xl sm:text-4xl font-bold text-rose-600">
                      {gstSettings?.isEnabled ? `₹${totalAmount.toLocaleString()}` : `₹${discountedPrice.toLocaleString()}`}
                    </p>
                    <span className="bg-rose-600 text-white text-xs font-bold px-2 py-1 rounded">
                      {Math.round(discountPercentage)}% OFF
                    </span>
                  </div>
                  {gstSettings?.isEnabled && (
                    <p className="text-xs text-gray-600 mt-1">
                      ₹{discountedPrice.toLocaleString()} + GST ₹{gstAmount.toFixed(2)}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs sm:text-sm text-gray-600">You Save</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">₹{(originalPrice - discountedPrice).toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Plan Features */}
            {Array.isArray(planId.customFeatures) && planId.customFeatures.length > 0 && (
              <div className="mb-6">
                <p className="text-gray-900 font-bold mb-3 flex items-center gap-2">
                  <Star className="size-5 text-amber-500 fill-amber-500" />
                  Plan Features:
                </p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {planId.customFeatures.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                      <CheckCircle className="size-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bonus Features */}
            {(extraContacts > 0 || extraValidity > 0) && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                <p className="text-purple-900 font-bold text-sm mb-2">🎁 Bonus Included:</p>
                <div className="space-y-1">
                  {extraContacts > 0 && (
                    <p className="text-sm text-purple-700">✓ +{extraContacts} Extra Contact Views</p>
                  )}
                  {extraValidity > 0 && (
                    <p className="text-sm text-purple-700">✓ +{extraValidity} Days Extra Validity</p>
                  )}
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="space-y-3 mb-6">
              <BenefitItem text="Chat directly with unlimited matches" />
              <BenefitItem text="View hidden contact numbers instantly" />
              <BenefitItem text="Stand out with a highlighted profile badge" />
            </div>

            {/* CTA Button */}
            <button
              onClick={handlePayNow}
              disabled={isProcessing || remainingTime <= 0}
              className="w-full py-3.5 sm:py-4 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-3"
            >
              {isProcessing ? 'Processing...' : 'Claim Offer Now'}
              {!isProcessing && <ArrowRight className="size-5" />}
            </button>

            {/* No Thanks Link */}
            <button
              onClick={onClose}
              className="w-full text-gray-500 hover:text-gray-700 text-sm text-center transition-colors"
            >
              No thanks, I'll pay full price
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

// Time Box Component for Countdown
function TimeBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="bg-rose-50 rounded-lg p-3 text-center border border-rose-100">
      <div className="text-2xl md:text-3xl font-bold text-rose-600">
        {String(value).padStart(2, '0')}
      </div>
      <div className="text-[10px] md:text-xs text-gray-500 font-medium mt-1">
        {label}
      </div>
    </div>
  );
}

// Benefit Item Component
function BenefitItem({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 mt-0.5">
        <CheckCircle className="size-5 text-rose-600 fill-rose-100" />
      </div>
      <span className="text-sm text-gray-700">{text}</span>
    </div>
  );
}